'use client'

import {Suspense, useEffect, useRef} from 'react'
import {usePathname, useRouter, useSearchParams} from 'next/navigation'
import {enableVisualEditing, type HistoryAdapterNavigate} from '@sanity/visual-editing'

export function SanityVisualEditing() {
  return (
    <Suspense fallback={null}>
      <PresentationBridge />
    </Suspense>
  )
}

function PresentationBridge() {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const navigate = useRef<HistoryAdapterNavigate | undefined>(undefined)

  useEffect(() => {
    let mounted = false
    let refreshTimer: number | undefined
    let lastRefreshAt = 0
    let refreshInFlight = false
    let pendingResolve: (() => void) | undefined
    const MIN_REFRESH_INTERVAL_MS = 1500
    const mountTimer = window.setTimeout(() => {
      mounted = true
    }, 0)

    const refreshPreview = () => {
      if (!mounted) return Promise.resolve()
      if (refreshTimer) {
        window.clearTimeout(refreshTimer)
      }
      pendingResolve?.()

      return new Promise<void>((resolve) => {
        pendingResolve = resolve
        refreshTimer = window.setTimeout(() => {
          const now = Date.now()
          if (refreshInFlight || now - lastRefreshAt < MIN_REFRESH_INTERVAL_MS) {
            resolve()
            if (pendingResolve === resolve) {
              pendingResolve = undefined
            }
            return
          }
          refreshInFlight = true
          lastRefreshAt = now
          window.requestAnimationFrame(() => {
            router.refresh()
            window.setTimeout(() => {
              refreshInFlight = false
              resolve()
              if (pendingResolve === resolve) {
                pendingResolve = undefined
              }
            }, 250)
          })
        }, 300)
      })
    }

    // Reuse the existing visual-editing runtime rather than bundling the newer
    // runtime pulled in by next-sanity's framework wrapper.
    const disable = enableVisualEditing({
      refresh: refreshPreview,
      history: {
        subscribe: (notify) => {
          navigate.current = notify
          notify({
            type: 'replace',
            url: window.location.pathname + window.location.search + window.location.hash,
          })
          return () => {
            navigate.current = undefined
          }
        },
        update: (update) => {
          if (update.type === 'pop') {
            router.back()
            return
          }
          const url = new URL(update.url, window.location.origin)
          if (url.origin !== window.location.origin) return
          const href = url.pathname + url.search + url.hash
          if (update.type === 'push') router.push(href)
          else if (update.type === 'replace') router.replace(href)
        },
      },
    })

    return () => {
      mounted = false
      window.clearTimeout(mountTimer)
      if (refreshTimer) {
        window.clearTimeout(refreshTimer)
      }
      pendingResolve?.()
      disable()
    }
  }, [router])

  useEffect(() => {
    const notify = () =>
      navigate.current?.({
        type: 'push',
        url: window.location.pathname + window.location.search + window.location.hash,
      })
    notify()
    window.addEventListener('hashchange', notify)
    return () => window.removeEventListener('hashchange', notify)
  }, [pathname, searchParams])

  return null
}
